import {
  Avatar,
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import {
  authorInitials,
  authorLabel,
  MISSING,
  playerHandleSuffix,
  socialTimeLabel,
} from "@bw/shared/components";
import type { MediaEmbed, PostResponse } from "@bw/shared/types/generated";
import { renderMarkdown } from "@bw/shared/utils";
import { useRef, useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { hasSafeAvatar } from "../avatar";

export interface MaterialSocialPostCardProps {
  post: PostResponse;
  actions?: ReactNode;
  detailPath?: (postId: string) => string | null;
  profilePath?: (userId: string) => string | null;
  menu?: ReactNode;
}

export function MaterialSocialPostCard({
  post,
  actions,
  detailPath,
  profilePath,
  menu,
}: MaterialSocialPostCardProps) {
  const original = post.reposted_post ?? null;
  return (
    <Paper
      component="article"
      variant="outlined"
      sx={{
        width: "100%",
        overflow: "hidden",
        // A flat feed row separated by a divider at every width (no card-in-card).
        border: 0,
        borderBottom: 1,
        borderColor: "divider",
        borderRadius: 0,
        bgcolor: "transparent",
        pb: 0.5,
      }}
    >
      {original ? (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ alignItems: "center", px: 0.5, pt: 1.5 }}
        >
          <RepeatRoundedIcon color="action" fontSize="small" />
          <Typography variant="caption" color="text.secondary">
            {authorLabel(post.author_display_name, post.author_id)} reposted
          </Typography>
        </Stack>
      ) : null}

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: "center", px: 0.5, pt: 1.25 }}
      >
        <AuthorIdentity
          post={post}
          detailPath={detailPath}
          profilePath={profilePath}
        />
        {menu ? <Box sx={{ flexShrink: 0 }}>{menu}</Box> : null}
      </Stack>

      {post.content ? (
        <Box sx={{ px: 0.5, pt: 0.5, overflowWrap: "anywhere", "& p": { my: 0 } }}>
          {renderMarkdown(post.content)}
        </Box>
      ) : null}
      <MaterialSocialPostMedia media={post.media ?? []} />

      {original ? (
        <EmbeddedPost
          post={original}
          detailPath={detailPath}
          profilePath={profilePath}
        />
      ) : null}

      {actions ? <Box sx={{ pt: 0.25 }}>{actions}</Box> : null}
    </Paper>
  );
}

function AuthorIdentity({
  post,
  detailPath,
  profilePath,
  compact = false,
}: {
  post: PostResponse;
  detailPath?: (postId: string) => string | null;
  profilePath?: (userId: string) => string | null;
  compact?: boolean;
}) {
  const name = authorLabel(post.author_display_name, post.author_id);
  const initials = authorInitials(post.author_display_name);
  const avatarUrl = hasSafeAvatar(post.author_avatar_url)
    ? post.author_avatar_url
    : undefined;
  const profileHref = profilePath?.(post.author_id) ?? null;
  const detailHref = detailPath?.(post.post_id) ?? null;
  const time = socialTimeLabel(post.created_at);
  const identity = (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", minWidth: 0 }}
    >
      <Avatar
        src={avatarUrl}
        alt=""
        aria-hidden
        sx={{
          width: compact ? 28 : 36,
          height: compact ? 28 : 36,
          fontSize: "0.875rem",
        }}
      >
        {avatarUrl ? null : initials}
      </Avatar>
      {/* Only the name truncates; the #id always stays visible. */}
      <Stack direction="row" spacing={0.5} sx={{ minWidth: 0, alignItems: "baseline" }}>
        <Typography component="span" noWrap sx={{ fontWeight: 650, minWidth: 0 }}>
          {name}
        </Typography>
        {name !== MISSING ? (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {playerHandleSuffix(post.author_id)}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );

  const timeNode = time ? (
    detailHref ? (
      <Typography
        component={RouterLink}
        to={detailHref}
        aria-label={post.content ? `${name}: ${post.content}` : name}
        variant="body2"
        color="text.secondary"
        sx={{ textDecoration: "none" }}
      >
        <time dateTime={post.created_at ?? undefined}>{time}</time>
      </Typography>
    ) : (
      <Typography component="time" variant="body2" color="text.secondary" dateTime={post.created_at ?? undefined}>
        {time}
      </Typography>
    )
  ) : null;

  // One line: avatar, name #id · time · Edited.
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ minWidth: 0, flex: 1, alignItems: "center", flexWrap: "nowrap" }}>
      {profileHref ? (
        <Box
          component={RouterLink}
          to={profileHref}
          aria-label={name !== MISSING ? `${name} ${playerHandleSuffix(post.author_id)}` : name}
          sx={{ color: "text.primary", textDecoration: "none", minWidth: 0, overflow: "hidden" }}
        >
          {identity}
        </Box>
      ) : (
        identity
      )}
      {timeNode ? (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flexShrink: 0 }}>
          <Typography component="span" variant="body2" color="text.secondary" aria-hidden>
            ·
          </Typography>
          {timeNode}
        </Stack>
      ) : null}
      {post.edited_at ? (
        <Typography component="span" variant="body2" color="text.secondary" aria-label="Edited">
          · Edited
        </Typography>
      ) : null}
    </Stack>
  );
}

function EmbeddedPost({
  post,
  detailPath,
  profilePath,
}: {
  post: PostResponse;
  detailPath?: (postId: string) => string | null;
  profilePath?: (userId: string) => string | null;
}) {
  if (post.is_deleted) {
    return (
      <Paper
        component="blockquote"
        variant="outlined"
        aria-label="Reposted post"
        sx={{ mx: { xs: 0.5, sm: 2 }, mb: 1.5, p: 0, pl: 1.5, border: 0, borderLeft: 3, borderColor: "divider", borderRadius: 0 }}
      >
        <Typography color="text.secondary">
          This post is no longer available.
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper
      component="blockquote"
      variant="outlined"
      aria-label="Reposted post"
      sx={{ mx: { xs: 0.5, sm: 2 }, mb: 1.5, p: 0, pl: 1.5, border: 0, borderLeft: 3, borderColor: "divider", borderRadius: 0, bgcolor: "transparent" }}
    >
      <Typography component="span" variant="caption" color="text.secondary">
        Reposted from{" "}
        <Box component="span">
          {authorLabel(post.author_display_name, post.author_id)}
        </Box>
      </Typography>
      <Box sx={{ mt: 1 }}>
        <AuthorIdentity
          post={post}
          compact
          detailPath={detailPath}
          profilePath={profilePath}
        />
      </Box>
      {post.content ? (
        <Box sx={{ mt: 1 }}>{renderMarkdown(post.content)}</Box>
      ) : null}
      <MaterialSocialPostMedia media={post.media ?? []} inset />
    </Paper>
  );
}

export function MaterialSocialPostMedia({
  media,
  inset = false,
}: {
  media: MediaEmbed[];
  inset?: boolean;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const renderable = media.filter(
    (entry) =>
      entry.media_type === "image" ||
      (entry.media_type === "youtube" && Boolean(entry.thumbnail_url)),
  );
  if (renderable.length === 0) return null;

  const visible = renderable.slice(0, 4);
  const remaining = renderable.length - visible.length;
  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: visible.length === 1 ? "1fr" : "repeat(2, 1fr)",
          gap: 0.5,
          mx: inset ? 0 : 2,
          mb: 2,
          mt: inset ? 1.5 : 0,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {visible.map((entry, index) => (
          <ButtonBase
            key={`${entry.url}-${index}`}
            type="button"
            aria-haspopup="dialog"
            aria-label={
              entry.media_type === "youtube"
                ? "Play YouTube video"
                : `View image ${index + 1} of ${renderable.length}`
            }
            onClick={(event) => {
              triggerRef.current = event.currentTarget;
              setLightboxIndex(index);
            }}
            sx={{
              position: "relative",
              aspectRatio: visible.length === 1 ? "16 / 10" : "1",
              bgcolor: "action.hover",
            }}
          >
            <Box
              component="img"
              src={
                entry.media_type === "youtube"
                  ? (entry.thumbnail_url ?? "")
                  : entry.url
              }
              alt=""
              aria-hidden
              referrerPolicy="no-referrer"
              loading="lazy"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {entry.media_type === "youtube" ? (
              <Avatar
                sx={{
                  position: "absolute",
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.72),
                }}
              >
                <PlayArrowRoundedIcon />
              </Avatar>
            ) : null}
            {remaining > 0 && index === visible.length - 1 ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.6),
                  color: "common.white",
                  fontSize: "1.75rem",
                  fontWeight: 700,
                }}
              >
                +{remaining}
              </Box>
            ) : null}
          </ButtonBase>
        ))}
      </Box>
      <MediaViewer
        media={renderable}
        index={lightboxIndex}
        onNavigate={setLightboxIndex}
        onClose={() => {
          setLightboxIndex(null);
          const trigger = triggerRef.current;
          triggerRef.current = null;
          requestAnimationFrame(() => trigger?.focus());
        }}
      />
    </>
  );
}

function MediaViewer({
  media,
  index,
  onNavigate,
  onClose,
}: {
  media: MediaEmbed[];
  index: number | null;
  onNavigate: (index: number) => void;
  onClose: () => void;
}) {
  const entry = index == null ? null : media[index];
  const total = media.length;
  const youtubeUrl =
    entry?.media_type === "youtube" && entry.youtube_id
      ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(entry.youtube_id)}`
      : null;
  return (
    <Dialog
      open={index != null}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          "aria-label": "Image viewer",
          sx: { bgcolor: "common.black", minHeight: "70vh" },
        },
        backdrop: {
          "data-testid": "media-viewer-backdrop",
        } as Record<string, string>,
      }}
    >
      <IconButton
        autoFocus
        aria-label="Close"
        onClick={onClose}
        sx={{
          position: "absolute",
          zIndex: 2,
          top: 8,
          right: 8,
          color: "common.white",
          bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
        }}
      >
        <CloseRoundedIcon />
      </IconButton>
      {entry?.media_type === "image" ? (
        <Box
          component="img"
          src={entry.url}
          alt="Post image attachment"
          referrerPolicy="no-referrer"
          sx={{ width: "100%", height: "70vh", objectFit: "contain" }}
        />
      ) : youtubeUrl ? (
        <Box
          component="iframe"
          src={youtubeUrl}
          title="YouTube video player"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          allow="encrypted-media; picture-in-picture"
          allowFullScreen
          sx={{ width: "100%", height: "70vh", border: 0 }}
        />
      ) : null}
      {index != null && total > 1 ? (
        <>
          <IconButton
            aria-label="Previous image"
            onClick={() => onNavigate((index - 1 + total) % total)}
            sx={{
              position: "absolute",
              left: 8,
              top: "50%",
              color: "common.white",
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
            }}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
          <IconButton
            aria-label="Next image"
            onClick={() => onNavigate((index + 1) % total)}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              color: "common.white",
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.45),
            }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
        </>
      ) : null}
    </Dialog>
  );
}
